import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Plus, Pencil, Power, FileJson, Copy, Tag, CloudDownload, RefreshCw } from 'lucide-react';

const PROMO_TYPES = [
  { v: 'DayOfWeek', l: 'Day of Week' },
  { v: 'LOS', l: 'Length of Stay' },
  { v: 'StayPeriod', l: 'Stay Period' },
  { v: 'BookingWindow', l: 'Booking Window' },
];
const DISCOUNT_TYPES = [
  { v: 'PR', l: 'Percentage (PR)' },
  { v: 'PN', l: 'Amount / Night (PN)' },
  { v: 'PS', l: 'Amount / Stay (PS)' },
  { v: 'VA', l: 'Value Add (VA)' },
  { v: 'VAC', l: 'Value Add Combination (VAC)' },
];
const APPLY_TO = [
  { v: 'AN', l: 'All Nights' },
  { v: 'FN', l: 'First Night' },
  { v: 'LN', l: 'Last Night' },
];
// 1=Sunday .. 7=Saturday (documented Yatra numbering)
const WEEK = [
  { n: 1, l: 'Sun' }, { n: 2, l: 'Mon' }, { n: 3, l: 'Tue' }, { n: 4, l: 'Wed' },
  { n: 5, l: 'Thu' }, { n: 6, l: 'Fri' }, { n: 7, l: 'Sat' },
];

const emptyForm = () => ({
  name: '', promotion_type: 'DayOfWeek', internal_room_type_ids: [],
  yatra_rate_plan_ids: [], internal_rate_plan_ids: [],
  discount_type: 'PR', discount_value: '',
  stay_date_from: '', stay_date_to: '', booking_date_from: '', booking_date_to: '',
  apply_to: 'AN',
  length_of_stay: '', multiple_los_discount_value: '', days_of_week: [],
  booking_window: '', booking_window_end: '', only_on_stay_dates_in_bw: false,
  stay_period_from: '', stay_period_to: '', value_add_name: '', value_add_desc: '',
  stop_sell: false, message: '', cancellation_type_id: '', apply_on_extra_guest: false,
  exclude_from_overlap: false, on_stay_date_or_checkin: '', is_lmp: false, no_of_nights: '',
});

const YatraRuleTable = ({ rows, loading, kind }) => (
  <div className="rounded-lg border overflow-hidden">
    <table className="w-full text-sm" data-testid={`yatra-${kind}-table`}>
      <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
        <tr>
          <th className="px-3 py-2">Name</th>
          <th className="px-3 py-2">Discount</th>
          <th className="px-3 py-2">Valid From</th>
          <th className="px-3 py-2">Valid To</th>
          <th className="px-3 py-2">Stay From</th>
          <th className="px-3 py-2">Stay To</th>
          <th className="px-3 py-2">Stop Sell</th>
          <th className="px-3 py-2">Promotion ID</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {loading && <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>}
        {!loading && rows.length === 0 && (
          <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400" data-testid={`yatra-${kind}-empty`}>No {kind} rate rules.</td></tr>
        )}
        {!loading && rows.map((r, i) => (
          <tr key={r.promotionId || i} className="hover:bg-gray-50" data-testid={`yatra-${kind}-row-${r.promotionId || i}`}>
            <td className="px-3 py-2 font-medium">{r.promotionName || '—'}</td>
            <td className="px-3 py-2">{r.discountType || '—'}{r.discountValue ? ` · ${r.discountValue}` : ''}</td>
            <td className="px-3 py-2 whitespace-nowrap">{r.validFrom || '—'}</td>
            <td className="px-3 py-2 whitespace-nowrap">{r.validTo || '—'}</td>
            <td className="px-3 py-2 whitespace-nowrap">{r.stayPeriodFrom || '—'}</td>
            <td className="px-3 py-2 whitespace-nowrap">{r.stayPeriodTo || '—'}</td>
            <td className="px-3 py-2">
              <Badge variant={r.stopSell === 'Y' ? 'destructive' : 'secondary'}>{r.stopSell === 'Y' ? 'Yes' : 'No'}</Badge>
            </td>
            <td className="px-3 py-2 font-mono text-xs text-gray-500">{r.promotionId || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Promotions = ({ propertyId, apiBase }) => {
  const API = apiBase;
  const [promos, setPromos] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [hasHotelCode, setHasHotelCode] = useState(true);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editRoomId, setEditRoomId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [payload, setPayload] = useState(null);

  // Yatra Promotions (live sync) state
  const [yatraOpen, setYatraOpen] = useState(false);
  const [yatraLoading, setYatraLoading] = useState(false);
  const [yatraLive, setYatraLive] = useState([]);
  const [yatraExpired, setYatraExpired] = useState([]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchYatra = useCallback(async () => {
    if (!propertyId) return null;
    setYatraLoading(true);
    try {
      const { data } = await axios.get(`${API}/promotions/sync-yatra`, { params: { property_id: propertyId } });
      const live = data?.liveRateRules || [];
      const expired = data?.expiredRateRules || [];
      setYatraLive(live);
      setYatraExpired(expired);
      if (!data?.success && data?.error) toast.error(`Yatra sync: ${data.error}`);
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to fetch Yatra promotions');
      return null;
    } finally {
      setYatraLoading(false);
    }
  }, [propertyId, API]);

  const openYatra = () => { setYatraOpen(true); fetchYatra(); };

  const load = useCallback(async () => {
    if (!propertyId) return;
    setLoading(true);
    try {
      const [pr, ro] = await Promise.all([
        axios.get(`${API}/properties/${propertyId}/promotions`),
        axios.get(`${API}/properties/${propertyId}/yatra-room-options`),
      ]);
      setPromos(pr.data?.promotions || []);
      setRooms(ro.data?.rooms || []);
      setHasHotelCode(!!ro.data?.has_hotel_code);
    } catch (e) {
      toast.error('Failed to load promotions');
    } finally {
      setLoading(false);
    }
  }, [propertyId, API]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditingId(null); setEditRoomId(null); setForm(emptyForm()); setAdvOpen(false); setDrawerOpen(true); fetchYatra(); };
  const mapYatraDetail = (d, base) => {
    const digits = (s) => (s == null ? [] : String(s).split('').filter((c) => /[1-7]/.test(c)).map(Number));
    const applied = (d.ratePlanApplicabilityList || [])
      .filter((x) => x.rateRuleApplied === 'Y')
      .map((x) => x.ratePlanId)
      .filter(Boolean);
    let roomId = base.internal_room_type_id;
    if (applied.length) {
      const match = rooms.find((r) => (r.rate_plans || []).some((rp) => applied.includes(rp.yatra_rate_plan_id)));
      if (match) roomId = match.internal_room_type_id;
    }
    const roomPlanIds = (rooms.find((r) => r.internal_room_type_id === roomId)?.rate_plans || []).map((rp) => rp.yatra_rate_plan_id);
    const yatraRpIds = applied.filter((id) => roomPlanIds.length === 0 || roomPlanIds.includes(id));
    return {
      promotion_type:              d.promotionName ?? base.promotion_type,
      discount_type:               d.discountType ?? base.discount_type,
      discount_value:              d.discountValue ?? '',
      // Yatra push mapping: validFrom/To <- booking dates, stayPeriodFrom/To <- stay dates.
      booking_date_from:           d.validFrom ?? '',
      booking_date_to:             d.validTo ?? '',
      stay_date_from:              d.stayPeriodFrom ?? '',
      stay_date_to:                d.stayPeriodTo ?? '',
      apply_to:                    d.applyTo ?? base.apply_to,
      days_of_week:                digits(d.daysOfWeek),
      length_of_stay:              d.lengthOfStay ?? '',
      no_of_nights:                d.noOfNights ?? '',
      stay_period_from:            d.stayPeriodFrom ?? '',
      stay_period_to:              d.stayPeriodTo ?? '',
      stop_sell:                   d.stopSell === 'Y',
      apply_on_extra_guest:        d.applyOnExtraGuest === 'Y',
      exclude_from_overlap:        d.excludeFromOverlap === 'Y',
      is_lmp:                      d.isLmp === 'Y',
      on_stay_date_or_checkin:     d.onStayDateOrCheckin ?? base.on_stay_date_or_checkin,
      booking_window:              d.bookingWindow ?? '',
      booking_window_end:          d.bookingWindowEnd ?? '',
      multiple_los_discount_value: d.multipleLOSDiscountValue ?? base.multiple_los_discount_value,
      internal_room_type_id:       roomId || base.internal_room_type_id,
      yatra_rate_plan_ids:         yatraRpIds.length ? yatraRpIds : base.yatra_rate_plan_ids,
    };
  };

  const openEdit = async (p) => {
    setEditingId(p.id);
    setEditRoomId(p.internal_room_type_id || null);
    let base = { ...emptyForm(), ...p, discount_value: p.discount_value ?? '', days_of_week: p.days_of_week || [] };
    // Seed new date-range fields from stored values, with legacy valid_from/to fallback.
    base.booking_date_from = p.booking_date_from ?? p.valid_from ?? '';
    base.booking_date_to = p.booking_date_to ?? p.valid_to ?? '';
    base.stay_date_from = p.stay_date_from ?? '';
    base.stay_date_to = p.stay_date_to ?? '';
    const rid = p.yatra_rate_rule_id || p.promotionId;
    if (rid && propertyId) {
      try {
        const { data } = await axios.get(`${API}/promotions/yatra-detail`, { params: { promotion_id: rid, property_id: propertyId } });
        if (data?.detail) {
          base = { ...base, ...mapYatraDetail(data.detail, base) };
          toast.success('Loaded latest values from Yatra');
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not fetch Yatra detail — using saved data');
      }
    }
    // Single-room legacy promotion → load as a single-element array so the
    // user can add more room types.
    const roomId = base.internal_room_type_id || p.internal_room_type_id;
    base.internal_room_type_ids = roomId ? [roomId] : [];
    setEditRoomId(roomId || null);
    setForm(base);
    setAdvOpen(false);
    setDrawerOpen(true);
  };

  // Rooms currently selected (array of internal_room_type_id).
  const selectedRooms = rooms.filter((r) => (form.internal_room_type_ids || []).includes(r.internal_room_type_id));
  // Union of rate plans across all selected rooms, de-duplicated by Yatra id.
  const unionRatePlans = (() => {
    const seen = new Set();
    const out = [];
    selectedRooms.forEach((r) => (r.rate_plans || []).forEach((rp) => {
      if (!seen.has(rp.yatra_rate_plan_id)) { seen.add(rp.yatra_rate_plan_id); out.push(rp); }
    }));
    return out;
  })();

  const toggleRoomType = (roomId) => {
    setForm((f) => {
      const has = (f.internal_room_type_ids || []).includes(roomId);
      const nextRooms = has
        ? f.internal_room_type_ids.filter((x) => x !== roomId)
        : [...(f.internal_room_type_ids || []), roomId];
      // Prune any selected rate plans no longer offered by any remaining room.
      const validIds = new Set();
      rooms.filter((r) => nextRooms.includes(r.internal_room_type_id))
        .forEach((r) => (r.rate_plans || []).forEach((rp) => validIds.add(rp.yatra_rate_plan_id)));
      return {
        ...f,
        internal_room_type_ids: nextRooms,
        yatra_rate_plan_ids: f.yatra_rate_plan_ids.filter((id) => validIds.has(id)),
      };
    });
  };
  const toggleRatePlan = (yid) => {
    setForm((f) => {
      const has = f.yatra_rate_plan_ids.includes(yid);
      return { ...f, yatra_rate_plan_ids: has ? f.yatra_rate_plan_ids.filter((x) => x !== yid) : [...f.yatra_rate_plan_ids, yid] };
    });
  };
  const toggleDay = (n) => {
    setForm((f) => {
      const has = f.days_of_week.includes(n);
      return { ...f, days_of_week: has ? f.days_of_week.filter((x) => x !== n) : [...f.days_of_week, n] };
    });
  };

  const buildBody = (overrides = {}) => {
    const b = { ...form, ...overrides };
    // Backend contract is one room type per promotion record; strip the
    // multi-select array and send the per-room scalar instead.
    delete b.internal_room_type_ids;
    ['discount_value', 'booking_window', 'booking_window_end', 'no_of_nights'].forEach((k) => {
      if (b[k] === '' || b[k] === null) b[k] = undefined;
    });
    if (b.no_of_nights !== undefined) b.no_of_nights = Number(b.no_of_nights);
    // Booking Window requires onStayDateOrCheckin — default to "StayDate".
    if (b.promotion_type === 'BookingWindow' && !b.on_stay_date_or_checkin) {
      b.on_stay_date_or_checkin = 'StayDate';
    }
    // lengthOfStay is required (int, separate from no_of_nights) — default 1.
    const los = Number(b.length_of_stay);
    b.length_of_stay = Number.isFinite(los) && los >= 1 ? los : 1;
    return b;
  };

  const save = async () => {
    if (!hasHotelCode) { toast.error('Configure the Yatra Hotel Code in OTA Mapping first'); return; }
    if (!form.name.trim()) { toast.error('Promotion Name is required'); return; }
    if ((form.internal_room_type_ids || []).length === 0) { toast.error('Select at least one Room Type'); return; }
    if (form.yatra_rate_plan_ids.length === 0) { toast.error('Select at least one Rate Plan'); return; }
    if ((form.discount_type === 'VA' || form.discount_type === 'VAC') && !form.value_add_name.trim()) {
      toast.error('Value Add Name is required for VA/VAC'); return;
    }

    // Fan out: one promotion per selected room type, each carrying ONLY the
    // selected rate plans that actually belong to that room (correct Yatra
    // mapping). The record being edited is PUT to its original room; any other
    // selected room types are created as new promotions.
    const tasks = [];
    (form.internal_room_type_ids || []).forEach((roomId) => {
      const room = rooms.find((r) => r.internal_room_type_id === roomId);
      if (!room) return;
      const matched = (room.rate_plans || []).filter((rp) => form.yatra_rate_plan_ids.includes(rp.yatra_rate_plan_id));
      if (matched.length === 0) return;
      tasks.push({
        roomId,
        body: buildBody({
          internal_room_type_id: roomId,
          yatra_rate_plan_ids: matched.map((rp) => rp.yatra_rate_plan_id),
          internal_rate_plan_ids: matched.map((rp) => rp.internal_rate_plan_id).filter(Boolean),
        }),
      });
    });
    if (tasks.length === 0) { toast.error('No selected rate plan matches the selected room type(s)'); return; }

    setSaving(true);
    try {
      let ok = 0, fail = 0, yatraFail = 0;
      for (const t of tasks) {
        const isEditTarget = editingId && t.roomId === editRoomId;
        const url = isEditTarget
          ? `${API}/properties/${propertyId}/promotions/${editingId}`
          : `${API}/properties/${propertyId}/promotions`;
        try {
          const resp = isEditTarget ? await axios.put(url, t.body) : await axios.post(url, t.body);
          ok += 1;
          if ((resp.data?.yatra || {}).status !== 'success') yatraFail += 1;
        } catch (e) {
          fail += 1;
        }
      }
      if (fail === 0 && yatraFail === 0) {
        toast.success(`${ok} promotion${ok > 1 ? 's' : ''} saved on Yatra`);
      } else if (fail === 0) {
        toast.error(`${ok} saved, but ${yatraFail} Yatra push${yatraFail > 1 ? 'es' : ''} failed — open Yatra Payload`);
      } else {
        toast.error(`${ok} saved, ${fail} failed`);
      }
      setDrawerOpen(false);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to save promotion';
      toast.error(typeof detail === 'string' ? detail : 'Validation failed');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (p) => {
    try {
      await axios.delete(`${API}/properties/${propertyId}/promotions/${p.id}`);
      toast.success('Promotion deactivated');
      load();
    } catch { toast.error('Failed to deactivate'); }
  };

  const viewPayload = async (p) => {
    try {
      const r = await axios.get(`${API}/properties/${propertyId}/promotions/${p.id}/payload`);
      setPayload({ name: p.name, ...r.data });
    } catch { toast.error('No payload available'); }
  };

  const copy = (txt) => { navigator.clipboard?.writeText(txt || ''); toast.success('Copied'); };
  const prettyJson = (s) => { try { return JSON.stringify(JSON.parse(s), null, 2); } catch { return s || '—'; } };

  const statusBadge = (s) => {
    const map = { active: 'bg-emerald-100 text-emerald-800', failed: 'bg-red-100 text-red-800', inactive: 'bg-gray-100 text-gray-600' };
    return <Badge className={`${map[s] || 'bg-gray-100 text-gray-600'} capitalize`} data-testid={`promo-status-${s}`}>{s || 'unknown'}</Badge>;
  };
  const roomName = (id) => rooms.find((r) => r.internal_room_type_id === id)?.name || '—';
  const dtype = form.discount_type;

  return (
    <div className="space-y-6" data-testid="promotions-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Tag className="w-5 h-5 text-sky-600" /> Promotions</h2>
          <p className="text-sm text-gray-500">Yatra / Travelguru rate rules for this property</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openYatra} data-testid="yatra-promotions-btn" className="gap-2">
            <CloudDownload className="w-4 h-4" /> Yatra Promotions
          </Button>
          <Button onClick={openAdd} data-testid="add-promotion-btn" className="gap-2">
            <Plus className="w-4 h-4" /> Add Promotion
          </Button>
        </div>
      </div>

      {!hasHotelCode && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="promo-no-hotelcode">
          This property has no Yatra Hotel Code configured. Save it in OTA Mapping before creating promotions.
        </div>
      )}

      <div className="rounded-lg border bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="promotions-table">
          <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
            <tr>
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Room</th>
              <th className="px-4 py-3">Rate Plans</th><th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Discount</th><th className="px-4 py-3">Valid</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>}
            {!loading && promos.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-gray-400" data-testid="promotions-empty">No promotions yet. Click “Add Promotion”.</td></tr>
            )}
            {promos.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50" data-testid={`promo-row-${p.id}`}>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-gray-600">{roomName(p.internal_room_type_id)}</td>
                <td className="px-4 py-3 text-gray-600">{(p.yatra_rate_plan_ids || []).length}</td>
                <td className="px-4 py-3">{PROMO_TYPES.find((t) => t.v === p.promotion_type)?.l || p.promotion_type}</td>
                <td className="px-4 py-3">{p.discount_type}{p.discount_value ? ` · ${p.discount_value}` : ''}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.valid_from} → {p.valid_to}</td>
                <td className="px-4 py-3">{statusBadge(p.status)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => viewPayload(p)} data-testid={`promo-payload-btn-${p.id}`} title="Yatra Payload"><FileJson className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`promo-edit-btn-${p.id}`} title="Edit"><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => deactivate(p)} data-testid={`promo-deactivate-btn-${p.id}`} title="Deactivate"><Power className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit drawer */}
      <Dialog open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="promotion-drawer">
          <DialogHeader><DialogTitle>{editingId ? 'Edit Promotion' : 'Add Promotion'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Basic */}
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Promotion Name *</Label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="promo-name-input" />
              </div>
              <div>
                <Label>Promotion Type *</Label>
                <Select value={form.promotion_type} onValueChange={(v) => set('promotion_type', v)}>
                  <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROMO_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Room Type(s) *</Label>
                <div className="flex flex-wrap gap-2 mt-1 max-h-32 overflow-y-auto rounded-md border p-2" data-testid="promo-room-multiselect">
                  {rooms.length === 0 && <span className="text-sm text-gray-400">No Yatra-mapped room types.</span>}
                  {rooms.map((r) => (
                    <label key={r.internal_room_type_id} className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer ${(form.internal_room_type_ids || []).includes(r.internal_room_type_id) ? 'border-sky-500 bg-sky-50' : ''}`} data-testid={`promo-room-${r.internal_room_type_id}`}>
                      <Checkbox checked={(form.internal_room_type_ids || []).includes(r.internal_room_type_id)} onCheckedChange={() => toggleRoomType(r.internal_room_type_id)} />
                      {r.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Rate plans — union across all selected room types */}
            {selectedRooms.length > 0 && (
              <div>
                <Label>Rate Plan(s) *</Label>
                <div className="flex flex-wrap gap-2 mt-1" data-testid="promo-rateplans">
                  {unionRatePlans.length === 0 && <span className="text-sm text-gray-400">No Yatra rate plans mapped for the selected room type(s).</span>}
                  {unionRatePlans.map((rp) => (
                    <label key={rp.yatra_rate_plan_id} className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm cursor-pointer ${form.yatra_rate_plan_ids.includes(rp.yatra_rate_plan_id) ? 'border-sky-500 bg-sky-50' : ''}`} data-testid={`promo-rateplan-${rp.yatra_rate_plan_id}`}>
                      <Checkbox checked={form.yatra_rate_plan_ids.includes(rp.yatra_rate_plan_id)} onCheckedChange={() => toggleRatePlan(rp.yatra_rate_plan_id)} />
                      {rp.name} <span className="text-gray-400 font-mono text-xs">{rp.yatra_rate_plan_id}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Discount */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Discount Type *</Label>
                <Select value={form.discount_type} onValueChange={(v) => set('discount_type', v)}>
                  <SelectTrigger data-testid="promo-discount-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{DISCOUNT_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{dtype === 'PR' ? 'Discount %' : dtype === 'PN' ? 'Amount / Night' : dtype === 'PS' ? 'Amount / Stay' : 'Discount Value'}</Label>
                <Input type="number" value={form.discount_value} onChange={(e) => set('discount_value', e.target.value)} data-testid="promo-discount-value-input" />
              </div>
            </div>

            {/* Value Add */}
            {(dtype === 'VA' || dtype === 'VAC') && (
              <div className="grid grid-cols-2 gap-3" data-testid="promo-valueadd">
                <div><Label>Value Add Name *</Label><Input value={form.value_add_name} onChange={(e) => set('value_add_name', e.target.value)} data-testid="promo-valueadd-name-input" /></div>
                <div><Label>Value Add Description</Label><Input value={form.value_add_desc} onChange={(e) => set('value_add_desc', e.target.value)} data-testid="promo-valueadd-desc-input" /></div>
              </div>
            )}

            {/* Date ranges (both optional) + apply to */}
            <div className="grid grid-cols-2 gap-3" data-testid="promo-stay-date-row">
              <div><Label>Stay Date From</Label><Input type="date" value={form.stay_date_from} onChange={(e) => set('stay_date_from', e.target.value)} data-testid="promo-stay-date-from-input" /></div>
              <div><Label>Stay Date To</Label><Input type="date" value={form.stay_date_to} onChange={(e) => set('stay_date_to', e.target.value)} data-testid="promo-stay-date-to-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3" data-testid="promo-booking-date-row">
              <div><Label>Booking Date From</Label><Input type="date" value={form.booking_date_from} onChange={(e) => set('booking_date_from', e.target.value)} data-testid="promo-booking-date-from-input" /></div>
              <div><Label>Booking Date To</Label><Input type="date" value={form.booking_date_to} onChange={(e) => set('booking_date_to', e.target.value)} data-testid="promo-booking-date-to-input" /></div>
              <div>
                <Label>Apply To</Label>
                <Select value={form.apply_to} onValueChange={(v) => set('apply_to', v)}>
                  <SelectTrigger data-testid="promo-applyto-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{APPLY_TO.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Type-specific */}
            {form.promotion_type === 'LOS' && (
              <div className="grid grid-cols-2 gap-3" data-testid="promo-los-fields">
                <div><Label>Length of Stay</Label><Input type="number" value={form.length_of_stay} onChange={(e) => set('length_of_stay', e.target.value)} data-testid="promo-los-input" /></div>
                <div><Label>Multiple LOS Discounts (use | )</Label><Input placeholder="40|50" value={form.multiple_los_discount_value} onChange={(e) => set('multiple_los_discount_value', e.target.value)} data-testid="promo-multiplelos-input" /></div>
              </div>
            )}
            {form.promotion_type === 'DayOfWeek' && (
              <div data-testid="promo-dow-fields">
                <Label>Applicable Days</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  <label className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer ${form.days_of_week.length === WEEK.length ? 'border-sky-500 bg-sky-50' : ''}`} data-testid="promo-day-all">
                    <Checkbox checked={form.days_of_week.length === WEEK.length} onCheckedChange={(v) => set('days_of_week', v ? WEEK.map((d) => d.n) : [])} />All
                  </label>
                  {WEEK.map((d) => (
                    <label key={d.n} className={`flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer ${form.days_of_week.includes(d.n) ? 'border-sky-500 bg-sky-50' : ''}`} data-testid={`promo-day-${d.n}`}>
                      <Checkbox checked={form.days_of_week.includes(d.n)} onCheckedChange={() => toggleDay(d.n)} />{d.l}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {form.promotion_type === 'BookingWindow' && (
              <div className="grid grid-cols-3 gap-3" data-testid="promo-bw-fields">
                <div><Label>Booking Window</Label><Input type="number" value={form.booking_window} onChange={(e) => set('booking_window', e.target.value)} data-testid="promo-bw-input" /></div>
                <div><Label>Booking Window End</Label><Input type="number" value={form.booking_window_end} onChange={(e) => set('booking_window_end', e.target.value)} data-testid="promo-bwend-input" /></div>
                <div>
                  <Label>On Stay Date / Check-in <span className="text-red-500">*</span></Label>
                  <Select value={form.on_stay_date_or_checkin || 'StayDate'} onValueChange={(v) => set('on_stay_date_or_checkin', v)}>
                    <SelectTrigger data-testid="promo-staydate-select"><SelectValue placeholder="Stay Date" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="StayDate">Stay Date</SelectItem>
                      <SelectItem value="CheckIn">Check-in</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2 pb-2"><Checkbox checked={form.only_on_stay_dates_in_bw} onCheckedChange={(v) => set('only_on_stay_dates_in_bw', !!v)} data-testid="promo-onlybw-check" /><Label>Only stay dates in BW</Label></div>
              </div>
            )}
            {form.promotion_type === 'StayPeriod' && (
              <div className="grid grid-cols-2 gap-3" data-testid="promo-sp-fields">
                <div><Label>Stay Period From</Label><Input type="date" value={form.stay_period_from} onChange={(e) => set('stay_period_from', e.target.value)} data-testid="promo-sp-from-input" /></div>
                <div><Label>Stay Period To</Label><Input type="date" value={form.stay_period_to} onChange={(e) => set('stay_period_to', e.target.value)} data-testid="promo-sp-to-input" /></div>
              </div>
            )}

            {/* Advanced */}
            <div className="border rounded-md">
              <button type="button" className="w-full px-3 py-2 text-left text-sm font-medium text-gray-700" onClick={() => setAdvOpen((o) => !o)} data-testid="promo-advanced-toggle">
                {advOpen ? '▾' : '▸'} Advanced Settings
              </button>
              {advOpen && (
                <div className="p-3 border-t grid grid-cols-2 gap-3" data-testid="promo-advanced">
                  <div className="flex items-center gap-2"><Checkbox checked={form.stop_sell} onCheckedChange={(v) => set('stop_sell', !!v)} data-testid="promo-stopsell-check" /><Label>Stop Sell</Label></div>
                  <div className="flex items-center gap-2"><Checkbox checked={form.apply_on_extra_guest} onCheckedChange={(v) => set('apply_on_extra_guest', !!v)} data-testid="promo-ea-check" /><Label>Apply On Extra Guest</Label></div>
                  <div className="flex items-center gap-2"><Checkbox checked={form.exclude_from_overlap} onCheckedChange={(v) => set('exclude_from_overlap', !!v)} data-testid="promo-overlap-check" /><Label>Exclude From Overlap</Label></div>
                  <div className="flex items-center gap-2"><Checkbox checked={form.is_lmp} onCheckedChange={(v) => set('is_lmp', !!v)} data-testid="promo-lmp-check" /><Label>Last Minute Promotion</Label></div>
                  <div><Label>Message</Label><Input value={form.message} onChange={(e) => set('message', e.target.value)} data-testid="promo-message-input" /></div>
                  <div><Label>Cancellation Policy Id</Label><Input value={form.cancellation_type_id} onChange={(e) => set('cancellation_type_id', e.target.value)} data-testid="promo-cancel-input" /></div>
                  {form.promotion_type !== 'LOS' && (
                    <div><Label>Length of Stay</Label><Input type="number" min="1" placeholder="1" value={form.length_of_stay} onChange={(e) => set('length_of_stay', e.target.value)} data-testid="promo-los-input-adv" /></div>
                  )}
                  <div><Label>Number Of Nights</Label><Input type="number" value={form.no_of_nights} onChange={(e) => set('no_of_nights', e.target.value)} data-testid="promo-nights-input" /></div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrawerOpen(false)} data-testid="promo-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="promo-save-btn">{saving ? 'Saving…' : 'Save Promotion'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Yatra Payload viewer (JSON) */}
      <Dialog open={!!payload} onOpenChange={(o) => !o && setPayload(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="promo-payload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileJson className="w-5 h-5 text-sky-600" /> Yatra Payload — {payload?.name}
            </DialogTitle>
          </DialogHeader>
          {payload && (
            <div className="space-y-3 text-sm">
              <div className="flex flex-wrap gap-4">
                <span>Operation: <b>{payload.operation === 'edit_rate_rule' ? 'Edit Rate Rule' : payload.operation === 'add_rate_rule' ? 'Add Rate Rule' : '—'}</b></span>
                <span>HTTP: <b data-testid="promo-payload-http">{payload.http_status ?? '—'}</b></span>
                <span>Status: {statusBadge(payload.status)}</span>
              </div>
              {payload.error && <div className="rounded bg-red-50 border border-red-200 p-2 text-red-700 text-xs" data-testid="promo-payload-error">{payload.error}</div>}
              <div>
                <div className="flex items-center justify-between"><Label>Request JSON</Label><Button size="sm" variant="ghost" onClick={() => copy(payload.request_json)} data-testid="promo-copy-request"><Copy className="w-3.5 h-3.5" /></Button></div>
                <pre className="bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto text-xs" data-testid="promo-payload-request">{prettyJson(payload.request_json)}</pre>
              </div>
              <div>
                <div className="flex items-center justify-between"><Label>Response JSON</Label><Button size="sm" variant="ghost" onClick={() => copy(payload.response_json)} data-testid="promo-copy-response"><Copy className="w-3.5 h-3.5" /></Button></div>
                <pre className="bg-gray-900 text-gray-100 rounded p-3 overflow-x-auto text-xs" data-testid="promo-payload-response">{payload.response_json ? prettyJson(payload.response_json) : '— (no response received)'}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Yatra Promotions (live sync) viewer */}
      <Dialog open={yatraOpen} onOpenChange={setYatraOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="yatra-promotions-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudDownload className="w-5 h-5 text-sky-600" /> Yatra Promotions
              <Button size="sm" variant="ghost" onClick={fetchYatra} disabled={yatraLoading} data-testid="yatra-refresh-btn" title="Refresh">
                <RefreshCw className={`w-4 h-4 ${yatraLoading ? 'animate-spin' : ''}`} />
              </Button>
            </DialogTitle>
            <p className="text-xs text-gray-500">Rate rules fetched live from Yatra for this property</p>
          </DialogHeader>
          <Tabs defaultValue="live">
            <TabsList data-testid="yatra-tabs">
              <TabsTrigger value="live" data-testid="yatra-tab-live">Live ({yatraLive.length})</TabsTrigger>
              <TabsTrigger value="expired" data-testid="yatra-tab-expired">Expired ({yatraExpired.length})</TabsTrigger>
            </TabsList>
            <TabsContent value="live" className="mt-3">
              <YatraRuleTable rows={yatraLive} loading={yatraLoading} kind="live" />
            </TabsContent>
            <TabsContent value="expired" className="mt-3">
              <YatraRuleTable rows={yatraExpired} loading={yatraLoading} kind="expired" />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Promotions;
